import gsap from "gsap";
import { Container, Sprite } from "pixi.js";
import Scene from "./Scene";
import { ConfigParams } from "../GameConfig";
import CurrentJackpots from "../components/CurrentJackpots";
import { CurrentJackpotValue } from "../models/JackpotCurValue";
import { audioManager } from "../services/AudioManager";

type SceneCurrentJackpotProps = {
  parent: Container;
  config: ConfigParams;
  name: string;
};

const STAGE = {
  width: 1920,
  height: 1080,
};

class SceneCurrentJackpot extends Scene {
  private curTimeline: gsap.core.Timeline | null = null;
  private readonly curJackpots: CurrentJackpots;
  private readonly currentValues: CurrentJackpotValue[] = [];

  private valueD: {
    maxNumOfTimes: number;
    lastUpdated: number;
    updateTimes: number[];
    medianUpdateTime: number;
  } = {
    maxNumOfTimes: 10,
    lastUpdated: Date.now(),
    updateTimes: [],
    medianUpdateTime: 250,
  };

  constructor({ parent, config, name }: SceneCurrentJackpotProps) {
    super({
      parent: parent,
      name: name,
    });

    for (let i = 0; i < this.valueD.maxNumOfTimes; i++) {
      this.valueD.updateTimes.push(this.valueD.medianUpdateTime);
    }

    this.curJackpots = new CurrentJackpots();

    this.contentContainer.addChild(this.curJackpots);
    this.curJackpots.zIndex = this.topZ;

    // const infoMessages = new InfoMessages();
    // infoMessages.zIndex = 2;
    // this.contentContainer.addChild(infoMessages);

    // const plonk = Sprite.from(`_plonk_jackpot`);
    // this.bgContainer.addChild(plonk);
    // plonk.zIndex = 1;
    // if (this.bgImage) this.bgImage.y = 90;

    const title = Sprite.from(`jackpot-title`);
    title.anchor.set(0.5, 0.5);
    title.position.set(STAGE.width / 2 - 5, 222);

    const frame = Sprite.from(`jackpot-frame`);
    frame.anchor.set(0.5, 0.5);
    frame.position.set(STAGE.width / 2, STAGE.height / 2 + 62);

    this.contentContainer.addChild(title, frame);
  }

  show(): void {
    super.show();

    this.curJackpots.show();
  }

  hide(): void {
    super.hide();

    this.curJackpots.hideInstant();
  }

  fadeIn(): gsap.core.Timeline {
    if (this.curTimeline) {
      this.curTimeline.kill();
      this.curTimeline = null;
    }
    const tl = gsap.timeline({
      onStart: () => {
        this.contentContainer.alpha = 0;
      },
      onComplete: () => {
        if (this.curTimeline === tl) {
          this.curTimeline.kill();
          this.curTimeline = null;
        }
      },
    });

    tl.addLabel("start")
      .call(this.show)
      .to(this.contentContainer, {
        pixi: {
          alpha: 1,
        },
        duration: 0.5,
        ease: "sine.out",
      });

    this.curTimeline = tl;

    return this.curTimeline;
  }

  fadeOut(): gsap.core.Timeline {
    if (this.curTimeline) {
      this.curTimeline.kill();
      this.curTimeline = null;
    }

    const tl = gsap.timeline({
      onStart: () => {},
      onComplete: () => {
        if (this.curTimeline === tl) {
          this.curTimeline.kill();
          this.curTimeline = null;
        }
      },
    });

    tl.addLabel("start").to(this.contentContainer, {
      pixi: {
        alpha: 0,
      },
      duration: 0.5,
      ease: "sine.in",
    });

    this.curTimeline = tl;

    return tl;
  }

  public updateValues(curJps: CurrentJackpotValue[]): void {
    const now = Date.now();
    this.valueD.updateTimes.push(now - this.valueD.lastUpdated);
    while (this.valueD.updateTimes.length > this.valueD.maxNumOfTimes) {
      this.valueD.updateTimes.shift();
    }
    this.valueD.lastUpdated = now;

    // Calculate median update time
    const sortedTimes = [...this.valueD.updateTimes].sort((a, b) => a - b);
    const middleIndex = Math.floor(sortedTimes.length / 2);
    this.valueD.medianUpdateTime = sortedTimes[middleIndex];

    // Check for jackpot reset or increment before updating values
    for (const incoming of curJps) {
      const idx = this.currentValues.findIndex((v) => v.code === incoming.code);
      if (idx >= 0) {
        const oldValue = this.currentValues[idx].value;

        // Check if the value has reset to initial amount
        if (incoming.value < oldValue) {
          audioManager.onJackpotReset();
        }
        // Check if the value has increased
        else if (incoming.value > oldValue) {
          console.log(`Jackpot increment: ${oldValue} -> ${incoming.value}`);
          audioManager.onJackpotIncrement();
        }

        this.currentValues[idx].value = incoming.value;
      } else {
        this.currentValues.push({ ...incoming });
      }
    }

    // console.log(
    //   "[SceneCurrentJackpot] Update called. New values:",
    //   this.currentValues,
    // );
    console.log(this.valueD.medianUpdateTime, this.currentValues[0]);
    this.curJackpots.updateValues({
      values: this.currentValues,
      duration: this.valueD.medianUpdateTime / 1000,
    });
  }
}

export default SceneCurrentJackpot;
